import { Box, Paper, Group, Text } from '@mantine/core';
import dayjs from 'dayjs';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  ReferenceDot,
  TooltipProps,
} from 'recharts';

import classes from './line-area-sat-chart.module.css';

import { formatDate } from '@data-access';

// Custom tooltip component
const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <Paper p="md" shadow="sm" withBorder>
        <Text fw={500} mb={5}>
          {dayjs(label).format('DD/MM/YYYY')}
        </Text>
        {payload.map((entry) => {
          const value = entry.value as number | number[];
          return (
            <Group justify="space-between" key={entry.dataKey}>
              <Group>
                <Box
                  style={{
                    backgroundColor: entry.color,
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                  }}
                />
                <Text fs="sm">{entry.name}:</Text>
              </Group>
              <Text fs="sm">
                {typeof value === 'number'
                  ? value.toFixed(2)
                  : value[1].toFixed(2)}
              </Text>
            </Group>
          );
        })}
      </Paper>
    );
  }
  return null;
};

interface BareCompositeChartProps {
  data: {
    id: string;
    date: number;
    min: number;
    max: number[];
    mean: number;
  }[];
  sowingDate: number;
  harvestDate: number;
  previousHarvestDate: string | null;
  setSelectedHeatmap: (id: string) => void;
  selectedHeatmap: string | null;
  domain?: [number, number];
}

export const LineAreaSatChart: React.FC<BareCompositeChartProps> = ({
  data,
  sowingDate,
  harvestDate,
  previousHarvestDate,
  setSelectedHeatmap,
  selectedHeatmap,
  domain,
}) => {
  const selectedHeatmapX = data.find((d) => d.id === selectedHeatmap)?.date;
  const selectedHeatmapY = data.find((d) => d.id === selectedHeatmap)?.mean;

  const numberOfDays = dayjs(data[data.length - 1].date).diff(
    dayjs(data[0].date),
    'day',
  );

  return (
    <Box h={250} miw={50 * numberOfDays}>
      <ResponsiveContainer height="100%" width="100%">
        <ComposedChart
          data={data}
          onClick={(e) => {
            if (e?.activePayload?.[0]?.payload?.id) {
              setSelectedHeatmap(e.activePayload[0].payload.id);
            }
          }}
        >
          <CartesianGrid stroke="#f5f5f5" />
          <XAxis
            dataKey="date"
            domain={['auto', 'auto']}
            scale="time"
            tickFormatter={(ts) =>
              formatDate({
                date: ts,
                format: 'display',
              })
            }
            type="number"
          />
          <YAxis domain={domain} />
          <Tooltip content={<CustomTooltip />} />

          {/* Reference lines */}
          <ReferenceLine
            label="Semis"
            stroke="orange"
            strokeDasharray="3 3"
            x={sowingDate}
          />
          <ReferenceLine
            label="récolte"
            stroke="orange"
            strokeDasharray="3 3"
            x={harvestDate}
          />
          {previousHarvestDate ? (
            <ReferenceLine
              label="récolte du précédent"
              stroke="orange"
              strokeDasharray="3 3"
              x={previousHarvestDate}
            />
          ) : null}

          {/* Area: max */}
          <Area
            activeDot={{ className: classes.hideDot }}
            connectNulls={false}
            dataKey="max"
            dot={{ className: classes.hideDot }}
            fill="var(--mantine-color-pf-green-8)"
            fillOpacity={0.3}
            name="Maximum"
            stroke="var(--mantine-color-pf-green-8)" // pf-green.8
            strokeWidth={2}
            type="monotone"
          />

          {/* Line: min */}
          <Line
            activeDot={{
              className: classes.hideDot,
            }}
            connectNulls={false}
            dataKey="min"
            dot={{
              className: classes.hideDot,
            }}
            name="Minimum"
            stroke="#e53935" // red.8
            strokeWidth={2}
            type="monotone"
          />

          {/* Line: mean */}
          <Line
            activeDot={{
              r: 6,
              fill: 'white',
              stroke: 'var(--mantine-color-blue-8)',
            }}
            connectNulls={false}
            dataKey="mean"
            dot={{
              fill: 'var(--mantine-color-blue-8)',
              r: 4,
            }}
            name="Moyenne"
            stroke="var(--mantine-color-blue-8)" // blue.8
            strokeWidth={2}
            type="monotone"
          />

          {selectedHeatmapX ? (
            <ReferenceDot
              fill="white"
              r={6}
              stroke="var(--mantine-color-blue-8)"
              strokeWidth={2}
              x={selectedHeatmapX}
              y={selectedHeatmapY}
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
